import React from "react";
import { Panel, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setSettingsPage } from "../actions/session";
import EmptyPlaceholder from "../controls/EmptyPlaceholder";
import { useExtensionObjects } from "../ExtensionProvider";
import lazyRequire from "../util/lazyRequire";
import makeReactive from "../util/makeReactive";
import MainPage from "./MainPage";
const startupSettings = lazyRequire(() => require("../util/startupSettings"), "default");
const registerSettings = (_instanceGroup, title, component, props, visible, priority) => {
    return { title, component, props, visible, priority: priority || 100 };
};
export const Settings = () => {
    const { t } = useTranslation(["common"]);
    const dispatch = useDispatch();
    // Get extension objects using the hook instead of HOC
    const allSettingPages = useExtensionObjects(registerSettings);
    const useModernLayout = useSelector((state) => state.settings.window.useModernLayout);
    // In Modern UI, game-specific settings (those with a visible callback) are shown
    // in the dedicated Game Settings page, so exclude them here to avoid duplication.
    const settingPages = useModernLayout
        ? allSettingPages.filter((page) => page.visible === undefined)
        : allSettingPages;
    const settingsPage = useSelector((state) => state.session.base.settingsPage || undefined);
    const startupSettingsRef = React.useRef(makeReactive(startupSettings));
    const changeStartup = React.useCallback((key, value) => {
        startupSettingsRef.current[key] = value;
    }, []);
    const setCurrentPage = React.useCallback((eventKey) => {
        if (typeof eventKey === "string") {
            dispatch(setSettingsPage(eventKey));
        }
    }, [dispatch]);
    const sortByPriority = (lhs, rhs) => {
        return lhs.priority - rhs.priority;
    };
    const renderTabElement = (page, idx) => {
        const props = page.props ? page.props() : {};
        return (React.createElement(Panel, { key: idx },
            React.createElement(Panel.Body, null,
                idx !== 0 ? React.createElement("hr", { style: { marginTop: 0 } }) : null,
                React.createElement(page.component, { ...props, changeStartup: changeStartup, startup: startupSettingsRef.current }))));
    };
    const renderTab = (page) => {
        // Show both global settings and game-specific settings that are currently visible
        const elements = page.elements
            .filter((ele) => ele.visible === undefined || ele.visible())
            .sort((lhs, rhs) => lhs.priority - rhs.priority);
        const content = elements.length > 0 ? (React.createElement("div", null, elements.map(renderTabElement))) : (React.createElement(EmptyPlaceholder, { icon: "settings", subtext: t("Other games may require settings here."), text: t("Nothing to configure.") }));
        return (React.createElement(Tab, { eventKey: page.title, key: page.title, title: t(page.title) },
            React.createElement("div", null, content)));
    };
    const combined = settingPages.reduce((prev, current) => {
        const result = prev.slice();
        const existingPage = prev.find((ele) => ele.title === current.title);
        if (existingPage === undefined) {
            result.push({
                title: current.title,
                elements: [current],
                priority: current.priority,
            });
        }
        else {
            existingPage.elements.push(current);
            if (existingPage.priority === undefined ||
                current.priority < existingPage.priority) {
                existingPage.priority = current.priority;
            }
        }
        return result;
    }, []);
    // Filter out tabs that have no visible elements and sort by priority
    const visibleTabs = combined
        .filter((tabPage) => tabPage.elements.some((ele) => ele.visible === undefined || ele.visible()))
        .sort(sortByPriority);
    const page = visibleTabs.find((iter) => iter.title === settingsPage) !== undefined
        ? settingsPage
        : visibleTabs[0]?.title;
    return (React.createElement(MainPage, null,
        React.createElement(MainPage.Body, null,
            React.createElement(Tabs, { activeKey: page, id: "settings-tab", onSelect: setCurrentPage }, visibleTabs.map(renderTab)))));
};
export default Settings;
